import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom"
import ComingSoonOverlay from "../../components/Common/ComingSoonOverlay"
import ListingTabs from "../../components/Listing/ListingTabs"
import { get } from "../../api/axios"
import endpoints from "../../api/endpoints"
import { toastApiError } from "../../helpers/apiError"
import { CityHubBanner } from "../CityHub/CityHubPanels"
import { mapApiCityToCityHubCity } from "../CityHub/cityHubCity"
import PublicSiteLayout from "./PublicSiteLayout"
import {
  PUBLIC_CITY_DEFAULT_TAB,
  PUBLIC_CITY_TAB_ITEMS,
  cityMatchesPublicParam,
  getPublicCityTabByPath,
  getPublicCityUrlKey,
  publicCityTabPath,
} from "./publicCityPaths"

const tabPathFromLocation = pathname => {
  const parts = String(pathname || "")
    .split("/")
    .filter(Boolean)
  // /public/cities/:citySlug/:tab
  if (parts[0] === "public" && parts[1] === "cities" && parts[3]) {
    return parts[3]
  }
  return PUBLIC_CITY_DEFAULT_TAB
}

const extractCitiesList = payload => {
  const result = payload?.data?.result
  if (Array.isArray(result)) return result
  if (Array.isArray(result?.data)) return result.data
  return []
}

const cityFromLocationState = (locationState, citySlugParam) => {
  const fromState = locationState?.city
  if (fromState && cityMatchesPublicParam(fromState, citySlugParam)) {
    return fromState
  }
  return null
}

const toPublicCityView = city => {
  if (!city) return null
  const mapped = mapApiCityToCityHubCity(city.rawCity || city, {
    typeLabel: "City",
  })
  return {
    ...mapped,
    type: "City",
    isOwned: false,
    isVisitOnly: true,
  }
}

const placeholderCity = citySlugParam => ({
  id: null,
  slug: citySlugParam || null,
  name: "City",
  shortName: "City",
  type: "City",
  isOwned: false,
  isVisitOnly: true,
  banner: null,
  thumb: null,
  tagline: "Discover, connect, and create opportunities in your city.",
})

/**
 * PublicCitiesRedirect
 * Loads the first public city and sends guests to the default tab.
 */
export const PublicCitiesRedirect = () => {
  const [target, setTarget] = useState(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      try {
        const response = await get(endpoints.getCites, {
          params: {
            page: 1,
            per_page: 1,
            page_size: 1,
            sort_direction: "asc",
          },
          skipErrorToast: true,
        })
        if (cancelled) return
        if (!response?.data?.success) {
          setFailed(true)
          return
        }
        const first = extractCitiesList(response)[0]
        const mapped = mapApiCityToCityHubCity(first, { typeLabel: "City" })
        const urlKey = getPublicCityUrlKey(mapped)
        if (urlKey) {
          setTarget(publicCityTabPath(PUBLIC_CITY_DEFAULT_TAB, mapped))
        } else {
          setFailed(true)
        }
      } catch {
        if (!cancelled) setFailed(true)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [])

  if (failed) {
    return (
      <PublicSiteLayout>
        <div className="public-city-page public-city-page--empty">
          <h1>Cities coming soon</h1>
          <p>
            Amok Earth cities will appear here as City Editors come on board.
            Request an invite to join the network.
          </p>
          <a href="/request-invite" className="public-site-header__btn public-site-header__btn--invite">
            Request an Invite
          </a>
        </div>
      </PublicSiteLayout>
    )
  }

  if (!target) {
    return (
      <PublicSiteLayout>
        <div className="public-city-page public-city-page--loading" aria-busy="true">
          <div className="amok-skeleton public-city-page__skel-banner" />
          <div className="amok-skeleton public-city-page__skel-line" />
        </div>
      </PublicSiteLayout>
    )
  }

  return <Navigate to={target} replace />
}

export const PublicCityTabIndexRedirect = () => (
  <Navigate to={PUBLIC_CITY_DEFAULT_TAB} replace />
)

/**
 * PublicCityLayout
 * Banner + City Hub tabs only (no Arts Genome / aside widgets).
 * Listing clicks: guests → /request-invite; logged-in members → City Hub tab.
 * Explore Cities popup only.
 * URLs use city slug; listing APIs still use numeric city id.
 */
const PublicCityLayout = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { citySlug: citySlugParam } = useParams()
  const [isCityLoading, setIsCityLoading] = useState(true)
  const [selectedCity, setSelectedCity] = useState(() =>
    cityFromLocationState(location.state, citySlugParam),
  )
  const listScrollRootRef = useRef(null)

  const tabPath = tabPathFromLocation(location.pathname)
  const activeTab = getPublicCityTabByPath(tabPath)

  useEffect(() => {
    const fromState = cityFromLocationState(location.state, citySlugParam)
    if (!fromState) return
    setSelectedCity(prev =>
      prev && cityMatchesPublicParam(prev, citySlugParam) && prev.id === fromState.id
        ? prev
        : fromState,
    )
  }, [citySlugParam, location.state])

  useEffect(() => {
    if (!citySlugParam) {
      setIsCityLoading(false)
      return undefined
    }

    let cancelled = false

    const hydrateCity = async () => {
      if (
        selectedCity &&
        cityMatchesPublicParam(selectedCity, citySlugParam) &&
        selectedCity.banner &&
        selectedCity.id
      ) {
        setIsCityLoading(false)
        return
      }

      setIsCityLoading(true)
      try {
        let mapped = null

        try {
          const byKey = await get(endpoints.getPublicCity(citySlugParam), {
            skipErrorToast: true,
          })
          if (byKey?.data?.success && byKey.data.result) {
            mapped = toPublicCityView(byKey.data.result)
          }
        } catch {
          // Fall through to catalog lookup until single-city public API ships.
        }

        if (!mapped) {
          const response = await get(endpoints.getCites, {
            params: {
              search: "",
              page: 1,
              per_page: 100,
              page_size: 100,
              sort_direction: "asc",
            },
            skipErrorToast: true,
          })
          if (!cancelled && response?.data?.success) {
            const match = extractCitiesList(response).find(item =>
              cityMatchesPublicParam(
                { id: item?.id, slug: item?.slug },
                citySlugParam,
              ),
            )
            if (match) mapped = toPublicCityView(match)
          }
        }

        if (!cancelled) {
          if (mapped && (mapped.slug || mapped.id)) {
            setSelectedCity(mapped)
            // Canonicalize numeric-id URLs to slug when available.
            const canonical = getPublicCityUrlKey(mapped)
            if (
              canonical &&
              mapped.slug &&
              String(citySlugParam) !== String(mapped.slug)
            ) {
              navigate(
                publicCityTabPath(tabPath || PUBLIC_CITY_DEFAULT_TAB, mapped),
                { replace: true, state: { city: mapped } },
              )
            }
          } else if (
            !selectedCity ||
            !cityMatchesPublicParam(selectedCity, citySlugParam)
          ) {
            setSelectedCity(placeholderCity(citySlugParam))
          }
        }
      } catch (error) {
        if (!cancelled) toastApiError(error, "Failed to load city")
      } finally {
        if (!cancelled) setIsCityLoading(false)
      }
    }

    hydrateCity()
    return () => {
      cancelled = true
    }
    // Intentionally omit selectedCity — hydrate when slug changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [citySlugParam])

  const city = useMemo(() => {
    if (selectedCity && cityMatchesPublicParam(selectedCity, citySlugParam)) {
      return toPublicCityView(selectedCity) || selectedCity
    }
    if (citySlugParam) {
      return placeholderCity(citySlugParam)
    }
    return null
  }, [citySlugParam, selectedCity])

  const cityUrlKey = getPublicCityUrlKey(city)

  useEffect(() => {
    if (!cityUrlKey || !activeTab) return
    const tabRootPath = publicCityTabPath(
      tabPath || PUBLIC_CITY_DEFAULT_TAB,
      city,
    )
    if (
      location.pathname === tabRootPath ||
      location.pathname.startsWith(`${tabRootPath}/`)
    ) {
      return
    }
    navigate(tabRootPath, { replace: true, state: { city } })
  }, [activeTab, city, cityUrlKey, location.pathname, navigate, tabPath])

  const handleSelectCity = useCallback(
    nextCity => {
      if (!getPublicCityUrlKey(nextCity)) return
      const resolved = toPublicCityView(nextCity) || nextCity
      setSelectedCity(resolved)
      navigate(
        publicCityTabPath(tabPath || PUBLIC_CITY_DEFAULT_TAB, resolved),
        { state: { city: resolved } },
      )
    },
    [navigate, tabPath],
  )

  if (!activeTab) {
    return (
      <Navigate
        to={publicCityTabPath(
          PUBLIC_CITY_DEFAULT_TAB,
          city || citySlugParam,
        )}
        replace
      />
    )
  }

  const outletContext = {
    tab: activeTab,
    location,
    city,
    yourCities: [],
    isCityMember: false,
    isCityLoading,
    isPublic: true,
    listScrollRootRef,
    onSelectCity: handleSelectCity,
    refreshRecentPhotos: undefined,
  }

  return (
    <PublicSiteLayout>
      <div className="public-city-page">
        <div
          className={`city-hub city-hub--public${
            activeTab.key === "calls-for-artists" ? " city-hub--calls" : ""
          }`}
        >
          <CityHubBanner
            city={city}
            yourCities={[]}
            isLoading={isCityLoading}
            onSelectCity={handleSelectCity}
            canExplore
            isPublic
          />

          <div className="city-hub__main">
            <div className="city-hub__tabs-row">
              <ListingTabs
                items={PUBLIC_CITY_TAB_ITEMS}
                activeKey={activeTab.path}
                onChange={nextPath => {
                  const nextTab = getPublicCityTabByPath(nextPath)
                  if (nextTab) {
                    navigate(publicCityTabPath(nextTab, city))
                  }
                }}
              />
            </div>

            <div
              className="city-hub__public-scroll"
              ref={listScrollRootRef}
            >
              {activeTab.comingSoon ? (
                <ComingSoonOverlay>
                  <Outlet context={outletContext} />
                </ComingSoonOverlay>
              ) : (
                <Outlet context={outletContext} />
              )}
            </div>
          </div>
        </div>
      </div>
    </PublicSiteLayout>
  )
}

export default PublicCityLayout
